import { cn } from "cn";
import type * as React from "react";

/** The colour a meter takes at a share used: ink, amber from 90%, red at 100%. */
export const meterTone = (share: number): "ink" | "warn" | "rec" =>
  share >= 1 ? "rec" : share >= 0.9 ? "warn" : "ink";

const FILL = {
  ink: "bg-v3-ink",
  warn: "bg-v3-warn",
  rec: "bg-v3-rec",
} as const;

/**
 * A usage meter (minutes used this period): an 8px track on the sunken
 * surface. `value` is the share used, 0 to 1; a sliver stays visible at 0.
 * The tone follows the share unless given.
 */
export function Meter({
  value,
  label,
  tone,
  className,
}: {
  value: number;
  /** The meter's accessible name. */
  label: string;
  tone?: keyof typeof FILL;
  className?: string;
}): React.JSX.Element {
  const share = Math.min(Math.max(value, 0), 1);
  return (
    <div
      aria-label={label}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(share * 100)}
      className={cn(
        "h-2 overflow-hidden rounded-full bg-v3-surface-3",
        className,
      )}
      data-slot="meter"
      role="progressbar"
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width] duration-500",
          FILL[tone ?? meterTone(share)],
        )}
        style={{ width: `${Math.max(share * 100, 2)}%` }}
      />
    </div>
  );
}
